import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import { v3Api } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import { useV3Socket, useSocketEvent } from '../hooks/useV3Socket';
import PropertyFilter from '../Shared/PropertyFilter';
import AnomaliesFilters from './AnomaliesFilters';
import AnomaliesTable from './AnomaliesTable';
import AnomalyDetailPanel from './AnomalyDetailPanel';
import './anomalies.css';

/**
 * /v3/anomalies — Anomaly Management UI (Sprint 3.6 FC2-FC4).
 *
 * Layout: header + filter rail + table. Detail panel slides in on row click.
 */
export default function AnomaliesPage() {
    const { t } = useTranslation();
    const { concessionaireId } = useAuth();
    const [selectedProperties, setSelectedProperties] = useState([]);
    const [filters, setFilters] = useState({ status: 'active' });
    const [page, setPage] = useState(1);
    const pageSize = 25;

    const [data, setData] = useState([]);
    const [meta, setMeta] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [selectedAlert, setSelectedAlert] = useState(null);
    const [toast, setToast] = useState(null);

    const showToast = useCallback((message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 2500);
    }, []);

    const buildParams = useCallback(() => {
        const params = { page, pageSize };
        if (concessionaireId) params.concessionaire_id = concessionaireId;
        if (selectedProperties.length === 1) params.property_id = selectedProperties[0];
        else if (selectedProperties.length > 1) params.property_id = selectedProperties.join(',');
        if (filters.status && filters.status !== 'all') params.status = filters.status;
        else if (filters.status === 'all') params.status = 'all';
        if (filters.tenant_id) params.tenant_id = filters.tenant_id;
        if (Array.isArray(filters.alert_type) && filters.alert_type.length) {
            params.alert_type = filters.alert_type.join(',');
        }
        if (Array.isArray(filters.severity) && filters.severity.length) {
            params.severity = filters.severity.join(',');
        }
        if (filters.from) params.from = new Date(filters.from).toISOString();
        if (filters.to)   params.to   = new Date(filters.to).toISOString();
        return params;
    }, [concessionaireId, selectedProperties, filters, page, pageSize]);

    const fetchList = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await v3Api.anomalies.list(buildParams());
            setData(res?.data || []);
            setMeta(res?.meta || {});
        } catch (e) {
            setError(e?.response?.data?.error || e?.message || t('Failed to load anomalies'));
        } finally {
            setLoading(false);
        }
    }, [buildParams, t]);

    useEffect(() => { fetchList(); }, [fetchList]);

    // Realtime: backend emits `anomaly.detected` to room concessionaire:<id>
    // when the scan-anomalies job persists a new alert. We refetch (debounced)
    // and surface a toast so the user sees fresh alerts without refreshing.
    const { connected: liveConnected } = useV3Socket();
    const refetchRef = useRef(fetchList);
    useEffect(() => { refetchRef.current = fetchList; }, [fetchList]);
    const refetchTimerRef = useRef(null);
    const scheduleLiveRefetch = useCallback(() => {
        if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current);
        refetchTimerRef.current = setTimeout(() => {
            refetchTimerRef.current = null;
            const fn = refetchRef.current;
            if (typeof fn === 'function') fn();
        }, 500);
    }, []);
    useEffect(() => () => {
        if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current);
    }, []);

    useSocketEvent('anomaly.detected', useCallback((payload = {}) => {
        const name = payload.tenant_name || t('a tenant');
        showToast(t('New anomaly detected · {{name}}', { name }), 'info');
        scheduleLiveRefetch();
    }, [showToast, scheduleLiveRefetch, t]));

    const handleFiltersChange = useCallback((next) => {
        setPage(1);
        setFilters(next);
    }, []);

    const handleSelect = useCallback((alert) => {
        setSelectedAlert(alert);
    }, []);

    const handleDetailChange = useCallback(() => {
        // Refetch the list after ack/resolve/reopen
        fetchList();
    }, [fetchList]);

    const counts = meta?.counts || {};
    const subtitle = useMemo(() => {
        const parts = [];
        if (counts.active != null) parts.push(t('{{count}} active', { count: counts.active }));
        const critical = counts.bySeverity?.critical;
        if (critical) parts.push(t('{{count}} critical', { count: critical }));
        const invGap = counts.byType?.invoice_gap;
        if (invGap) parts.push(invGap === 1
            ? t('{{count}} invoice gap', { count: invGap })
            : t('{{count}} invoice gaps', { count: invGap }));
        return parts.length ? parts.join(' · ') : t('Anomaly detection across your portfolio');
    }, [counts, t]);

    return (
        <div className="v3 v3-anomalies-page">
            <div className="v3-anomalies-header">
                <div>
                    <h1 className="v3-anomalies-title">{t('Anomaly Detection')}</h1>
                    <div className="v3-anomalies-subtitle">{subtitle}</div>
                </div>
                <div className="v3-anomalies-actions">
                    <span
                        className={`v3-anomalies-live ${liveConnected ? 'on' : 'off'}`}
                        title={liveConnected ? t('Live updates connected') : t('Live updates offline')}
                    >
                        <span className="v3-anomalies-live-dot" />
                        {liveConnected ? t('Live') : t('Offline')}
                    </span>
                    <PropertyFilter value={selectedProperties} onChange={setSelectedProperties} />
                    <button
                        className="v3-btn v3-btn-ghost"
                        onClick={fetchList}
                        disabled={loading}
                        type="button"
                    >
                        <RefreshCw size={14} className={loading ? 'spin' : ''} />
                        {t('Refresh')}
                    </button>
                </div>
            </div>

            {error && (
                <div className="v3-card" style={{ padding: 14, color: 'var(--v3-text-danger)', marginBottom: 14 }}>
                    {error}
                </div>
            )}

            <div className="v3-anomalies-body">
                <AnomaliesFilters filters={filters} onChange={handleFiltersChange} />
                <AnomaliesTable
                    alerts={data}
                    loading={loading}
                    total={meta?.total || 0}
                    page={page}
                    pageSize={pageSize}
                    onPage={(p) => setPage(Math.max(1, p))}
                    selectedId={selectedAlert?.id || selectedAlert?._id}
                    onSelect={handleSelect}
                />
            </div>

            {selectedAlert && (
                <AnomalyDetailPanel
                    alertSummary={selectedAlert}
                    onClose={() => setSelectedAlert(null)}
                    onChange={handleDetailChange}
                    onToast={showToast}
                />
            )}

            {toast && (
                <div className={`v3-anomalies-toast ${toast.type}`}>{toast.message}</div>
            )}
        </div>
    );
}
